import { isEmpty } from 'lodash';
import { setResult } from './resultActions';
import MazeServiceClient from '../clients/MazeServiceClient';
import selectors from '../../rootSelectors';
import { MAZE, DATA_SOURCE, COMPREHENSION } from '../../constants/measure';
import {
    makeApiGetRequest,
    makeCacheablePostResultsRequest,
    makeApiPostRequest,
} from './apiActions';
import { RESULTS_STORE_NAME } from '../persistence/constants';
import { getPersistenceStorages, getStudentResultKey } from '../persistence/storage';
import { removeResultFromStorage } from '../persistence/persistanceActions';
import { RESULTS_ERRORS } from '../../constants/error';
import { MAZE_PASSAGE_DURATION } from '../timeTracking/timeTrackingKeys';

export const saveResultResponse = postBody => dispatch => {
    const endpoint = MazeServiceClient.constructSaveResultEnpoint();
    const { measure, student_uid: studentUid } = postBody;
    return dispatch(
        makeCacheablePostResultsRequest(endpoint, studentUid, measure, postBody),
    ).then(resultJson => dispatch(setResult(resultJson)));
};

export const saveOfflineResponse = studentUid => async dispatch => {
    const endpoint = MazeServiceClient.constructSaveResultEnpoint();
    const persistenceStorages = getPersistenceStorages();
    const postBody = await persistenceStorages[RESULTS_STORE_NAME].getItem(
        getStudentResultKey(studentUid, MAZE.MEASURE),
    );
    if (!postBody) {
        return null;
    }
    // On offline results sync we show error popup on fail
    return dispatch(makeApiPostRequest(endpoint, JSON.parse(postBody)))
        .then(response => dispatch(setResult(response)))
        .then(() => dispatch(removeResultFromStorage(studentUid, MAZE.MEASURE)))
        .catch(error => {
            const { statusCode } = error || {};
            if (RESULTS_ERRORS.includes(statusCode)) {
                dispatch(removeResultFromStorage(studentUid, MAZE.MEASURE));
            }
        });
};

export const saveMeasureResponse = (formId, resultResponse) => async (dispatch, getStore) => {
    const state = getStore();
    const attemptNumber = selectors.getAttemptNumber(state, MAZE.MEASURE, COMPREHENSION);
    const instId = selectors.getInstId(state, MAZE.MEASURE);
    const secure = selectors.isAssessmentSecure(state, MAZE.MEASURE);
    const assessmentDuration = selectors.getTimeTrackingDuration(state, MAZE_PASSAGE_DURATION);
    const studentUid = selectors.getUserId(state);
    const isMazeUntimed = selectors.isMazeUntimed(state);

    const postBody = {
        assessment: MAZE.MEASURE,
        data_source: secure ? DATA_SOURCE.SECURE_ASSESSMENT : DATA_SOURCE.ONLINE,
        inst_uid: instId,
        student_uid: studentUid,
        student_temporal_id: selectors.getStudentTemporalId(state, instId) || undefined, // unset if not available
        school_year: selectors.getSchoolYear(state),
        grade_level: selectors.getGrade(state),
        period: selectors.getToy(state),
        measure: MAZE.MEASURE,
        form_id: formId,
        responses: resultResponse,
        skill_name: 'Basic Comprehension',
        assessment_duration: assessmentDuration,
        is_maze_untimed: isMazeUntimed,
        ...(attemptNumber === 2 && {
            attempt_number: 2,
            is_next_attempt_enabled: undefined,
        }),
        ...(attemptNumber === 3 && {
            attempt_number: 3,
            is_next_attempt_enabled: undefined,
        }),
    };

    return dispatch(saveResultResponse(postBody));
};

export const getStudentResults = () => (dispatch, getStore) => {
    const state = getStore();
    const studentId = selectors.getUserId(state);
    const grade = selectors.getGrade(state);
    const period = selectors.getToy(state);
    const schoolYear = selectors.getSchoolYear(state);
    const config = {
        studentId,
        grade,
        period,
        schoolYear,
    };
    const endpoint = MazeServiceClient.constructResultListEndpoint(config);
    return dispatch(makeApiGetRequest(endpoint)).then(data => {
        const resultsData = isEmpty(data) ? [] : data;
        resultsData.forEach(resultData => {
            dispatch(setResult(resultData));
        });
    });
};

export const getMeasureInstruction = instruction => dispatch =>
    dispatch(makeApiGetRequest(instruction));
